import React, { useState } from 'react';
import { Box, Typography, IconButton, Divider, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Person as PersonIcon, Group as GroupIcon } from '@mui/icons-material';
import { Conversation } from '../../types/chat';
import ConversationItem from './ConversationItem';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: number;
  loading?: boolean;
  onSelect: (conversation: Conversation) => void;
  onNewConversation?: () => void;
  onNewGroupConversation?: () => void;
  onRefresh?: () => void;
  onToggleStar?: (conversationId: number) => void;
  onToggleArchive?: (conversationId: number) => void;
  onDelete?: (conversationId: number) => void;
  onRestore?: (conversationId: number) => void;
}

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedId,
  loading = false,
  onSelect,
  onNewConversation,
  onNewGroupConversation,
  onRefresh,
  onToggleStar,
  onToggleArchive,
  onDelete,
  onRestore,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleNewConversation = () => {
    handleMenuClose();
    onNewConversation?.();
  };

  const handleNewGroupConversation = () => {
    handleMenuClose();
    onNewGroupConversation?.();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: OUTLOOK_BLUE }}>
          Messages
        </Typography>
        <Box>
          {onRefresh && (
            <IconButton size="small" onClick={onRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={handleAddClick}
            sx={{
              ml: 0.5,
              backgroundColor: OUTLOOK_BLUE,
              color: 'white',
              '&:hover': { backgroundColor: '#106ebe' },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>

          {/* Add menu */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { borderRadius: 2, minWidth: 180 }
            }}
          >
            {onNewConversation && (
              <MenuItem onClick={handleNewConversation}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Nouvelle conversation</ListItemText>
              </MenuItem>
            )}
            {onNewGroupConversation && (
              <MenuItem onClick={handleNewGroupConversation}>
                <ListItemIcon>
                  <GroupIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Nouveau groupe</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>

      {/* Conversation list */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading && conversations.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress size={32} sx={{ color: OUTLOOK_BLUE }} />
          </Box>
        ) : conversations.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              px: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              No conversations yet
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', mt: 1 }}>
              Click the + button to start a new conversation
            </Typography>
          </Box>
        ) : (
          conversations.map((conversation, index) => (
            <React.Fragment key={conversation.id}>
              <ConversationItem
                conversation={conversation}
                selected={conversation.id === selectedId}
                onClick={() => onSelect(conversation)}
                onToggleStar={onToggleStar}
                onToggleArchive={onToggleArchive}
                onDelete={onDelete}
                onRestore={onRestore}
              />
              {index < conversations.length - 1 && (
                <Divider sx={{ mx: 2 }} />
              )}
            </React.Fragment>
          ))
        )}
      </Box>
    </Box>
  );
};

export default ConversationList;
